"use client";

import * as React from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";

import { useIsMobile } from "../../hooks/use-mobile";
import { cn } from "../../lib/utils";
import { Button, buttonVariants } from "./button";
import { Input } from "./input";
import { Separator } from "./separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "./sheet";
import { Skeleton } from "./skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";
import { IconLayoutSidebar } from "@tabler/icons-react";
import { useMountEffect } from "../../hooks/use-mount-effect";

const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = 256;
const SIDEBAR_MIN_WIDTH = 224;
const SIDEBAR_MAX_WIDTH = 384;
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3.25rem";
const SIDEBAR_MENU_ITEM_GAP = "1px";
const SIDEBAR_MENU_BUTTON_GAP = "0.125rem";
const SIDEBAR_MENU_BUTTON_HEIGHT = "1.875rem";
const SIDEBAR_MENU_BUTTON_HEIGHT_SM = "1.75rem";
const SIDEBAR_MENU_BUTTON_HEIGHT_LG = "2.75rem";
const SIDEBAR_BUTTON_Y_PADDING_ONE_LINE = "0";
const SIDEBAR_BUTTON_Y_PADDING_TWO_LINE = "0.5rem";
const SIDEBAR_CONTENT_BUTTON_X_PADDING = "0.5rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

type SidebarContextProps = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = React.use(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }

  return context;
}

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);
  const [sidebarWidth, setSidebarWidth] = React.useState(SIDEBAR_WIDTH);

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // This sets the cookie to keep the sidebar state.
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open],
  );

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen, setOpenMobile]);
  const toggleSidebarRef = React.useRef(toggleSidebar);
  toggleSidebarRef.current = toggleSidebar;

  // Adds a keyboard shortcut to toggle the sidebar.
  useMountEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebarRef.current();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  // We add a state so that we can do data-state="expanded" or "collapsed".
  const state = open ? "expanded" : "collapsed";

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      sidebarWidth,
      setSidebarWidth,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar, sidebarWidth],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        data-slot="sidebar-wrapper"
        style={
          {
            "--sidebar-width": `${sidebarWidth}px`,
            "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
            "--sidebar-menu-item-gap": SIDEBAR_MENU_ITEM_GAP,
            "--sidebar-menu-button-gap": SIDEBAR_MENU_BUTTON_GAP,
            "--sidebar-menu-button-height": SIDEBAR_MENU_BUTTON_HEIGHT,
            "--sidebar-menu-button-height-sm": SIDEBAR_MENU_BUTTON_HEIGHT_SM,
            "--sidebar-menu-button-height-lg": SIDEBAR_MENU_BUTTON_HEIGHT_LG,
            "--sidebar-menu-button-two-line-height":
              SIDEBAR_MENU_BUTTON_HEIGHT_LG,
            "--sidebar-button-y-padding-one-line":
              SIDEBAR_BUTTON_Y_PADDING_ONE_LINE,
            "--sidebar-button-y-padding-two-line":
              SIDEBAR_BUTTON_Y_PADDING_TWO_LINE,
            "--sidebar-content-button-x-padding":
              SIDEBAR_CONTENT_BUTTON_X_PADDING,
            ...style,
          } as React.CSSProperties
        }
        className={cn(
          "[display:flex] [min-height:100svh] [width:100%]",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
}

function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  dir,
  ...props
}: React.ComponentProps<"div"> & {
  side?: "left" | "right";
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon" | "none";
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === "none") {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          "[display:flex] [height:100%] [width:var(--sidebar-width)] [flex-direction:column] [background-color:var(--sidebar)] [color:var(--sidebar-foreground)]",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          dir={dir}
          data-sidebar="sidebar"
          data-slot="sidebar"
          data-mobile="true"
          className={
            "[width:var(--sidebar-width)] [background-color:var(--sidebar)] [padding:0rem] [color:var(--sidebar-foreground)] [&>button]:[display:none]"
          }
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}
        >
          <SheetHeader className={"sr-only"}>
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div
            className={
              "[display:flex] [height:100%] [width:100%] [flex-direction:column]"
            }
          >
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className={
        "[position:relative] [display:none] [color:var(--sidebar-foreground)] md:[display:block]"
      }
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        data-slot="sidebar-gap"
        className={cn(
          "[position:relative] [width:var(--sidebar-width)] [background-color:transparent]",
          "[[data-slot=sidebar][data-collapsible=offcanvas]_&]:[width:0rem]",
          "[[data-slot=sidebar][data-side=right]_&]:[rotate:180deg]",
          variant === "floating" || variant === "inset"
            ? "[[data-slot=sidebar][data-collapsible=icon]_&]:[width:calc(var(--sidebar-width-icon)_+_1rem)]"
            : "[[data-slot=sidebar][data-collapsible=icon]_&]:[width:var(--sidebar-width-icon)]",
        )}
      />
      <div
        data-slot="sidebar-container"
        data-side={side}
        className={cn(
          "[position:fixed] [z-index:10] [display:none] [height:100svh] [width:var(--sidebar-width)] [[data-slot=sidebar][data-collapsible=offcanvas]_&[data-side=left]]:[left:calc(var(--sidebar-width)*-1)] [[data-slot=sidebar][data-collapsible=offcanvas]_&[data-side=right]]:[right:calc(var(--sidebar-width)*-1)] md:[display:flex]",
          // Adjust the padding for floating and inset variants.
          variant === "floating" || variant === "inset"
            ? "[padding:0.5rem] [[data-slot=sidebar][data-collapsible=icon]_&]:[width:calc(var(--sidebar-width-icon)_+_1rem_+_2px)]"
            : "[[data-slot=sidebar][data-collapsible=icon]_&]:[width:var(--sidebar-width-icon)] [[data-slot=sidebar][data-side=left]_&]:[border-right-width:1px] [[data-slot=sidebar][data-side=left]_&]:[border-right-style:solid] [[data-slot=sidebar][data-side=left]_&]:[border-right-color:var(--sidebar-border)] [[data-slot=sidebar][data-side=right]_&]:[border-left-width:1px] [[data-slot=sidebar][data-side=right]_&]:[border-left-style:solid] [[data-slot=sidebar][data-side=right]_&]:[border-left-color:var(--sidebar-border)]",
          className,
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className={
            "[display:flex] [width:100%] [height:100%] [flex-direction:column] [background-color:var(--sidebar)] [[data-slot=sidebar][data-variant=floating]_&]:[outline-color:var(--sidebar-border)]"
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function SidebarTrigger({
  "aria-label": ariaLabel = "Toggle sidebar",
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="ghost"
      size="icon-sm"
      aria-label={ariaLabel}
      className={cn(className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <IconLayoutSidebar />
    </Button>
  );
}

function SidebarRail({
  className,
  toggleOnClick = true,
  ...props
}: React.ComponentProps<"button"> & {
  toggleOnClick?: boolean;
}) {
  const { isMobile, sidebarWidth, setSidebarWidth, toggleSidebar } = useSidebar();
  const dragState = React.useRef({ active: false, moved: false });
  const [isDragging, setIsDragging] = React.useState(false);

  function handlePointerDown(event: React.PointerEvent<HTMLButtonElement>) {
    if (isMobile || event.button !== 0) return;

    const startX = event.clientX;
    const startWidth = sidebarWidth;
    dragState.current = { active: true, moved: false };
    setIsDragging(true);

    function handlePointerMove(moveEvent: PointerEvent) {
      const nextWidth = Math.min(
        SIDEBAR_MAX_WIDTH,
        Math.max(SIDEBAR_MIN_WIDTH, startWidth + moveEvent.clientX - startX),
      );
      if (Math.abs(moveEvent.clientX - startX) > 3) {
        dragState.current.moved = true;
      }
      setSidebarWidth(nextWidth);
    }

    function handlePointerUp() {
      dragState.current.active = false;
      setIsDragging(false);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    }

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp, { once: true });
    window.addEventListener("pointercancel", handlePointerUp, { once: true });
  }

  return (
    <button
      type="button"
      data-sidebar="rail"
      data-slot="sidebar-rail"
      data-dragging={isDragging}
      aria-label={toggleOnClick ? "Toggle Sidebar" : "Resize Sidebar"}
      tabIndex={-1}
      onPointerDown={handlePointerDown}
      onClick={(event) => {
        if (dragState.current.moved) {
          event.preventDefault();
          dragState.current.moved = false;
          return;
        }
        if (toggleOnClick) {
          toggleSidebar();
        }
      }}
      title={toggleOnClick ? "Toggle Sidebar" : "Resize Sidebar"}
      className={cn(
        "[position:absolute] [z-index:20] [inset-block:0rem] [display:none] [width:0.5rem] [[data-slot=sidebar][data-side=left]_&]:[right:calc(0.5rem_*_-1)] [[data-slot=sidebar][data-side=right]_&]:[left:calc(0.5rem_*_-1)] sm:[display:flex]",
        "[[data-side=left]_&]:[cursor:w-resize] [[data-side=right]_&]:[cursor:e-resize]",
        "[[data-side=left][data-state=collapsed]_&]:[cursor:e-resize] [[data-side=right][data-state=collapsed]_&]:[cursor:w-resize]",
        "after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:w-px after:bg-transparent after:transition-[width,background-color] hover:after:bg-primary [[data-slot=sidebar][data-side=right]_&]:after:right-0 [[data-slot=sidebar][data-side=right]_&]:after:left-auto data-[dragging=true]:after:w-[3px] data-[dragging=true]:after:bg-primary",
        "hover:[[data-slot=sidebar][data-collapsible=offcanvas]_&]:[background-color:var(--sidebar)]",
        className,
      )}
      {...props}
    />
  );
}

function SidebarInset({ className, ...props }: React.ComponentProps<"main">) {
  const { state } = useSidebar();

  return (
    <main
      data-slot="sidebar-inset"
      data-state={state}
      className={cn(
        "group/inset [position:relative] [display:flex] [width:100%] [min-width:0] [flex:1_1_0%] [flex-direction:column] [background-color:var(--background)]",
        state === "collapsed"
          ? "[&_[data-sidebar=inset-trigger]]:[position:absolute] [&_[data-sidebar=inset-trigger]]:[top:0.5rem] [&_[data-sidebar=inset-trigger]]:[left:0.5rem] [&_[data-sidebar=inset-trigger]]:[z-index:10]"
          : "[&_[data-sidebar=inset-trigger]]:[display:none]",
        className,
      )}
      {...props}
    />
  );
}

function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn(
        "[height:2rem] [width:100%] [background-color:var(--background)] [box-shadow:none]",
        className,
      )}
      {...props}
    />
  );
}

function SidebarHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn(
        "[display:flex] [flex-direction:column] [gap:0.5rem] [padding:0.5rem] [[data-slot=sidebar][data-collapsible=icon]_&]:[align-items:center] [[data-slot=sidebar][data-collapsible=icon]_&>div]:[flex-direction:column] [[data-slot=sidebar][data-collapsible=icon]_&>div]:[justify-content:center] [[data-slot=sidebar][data-collapsible=icon]_&>div>div]:[margin-left:0rem] [[data-slot=sidebar][data-collapsible=icon]_&>div:not(:first-child)]:[display:none] [[data-slot=sidebar][data-collapsible=icon]_&_[data-sidebar=header-actions]]:[display:none] [[data-slot=sidebar][data-collapsible=icon]_&_[data-sidebar=header-control]]:[display:none] [[data-slot=sidebar][data-collapsible=icon]_&_[data-sidebar=header-trigger]]:[display:none] [[data-slot=sidebar][data-collapsible=icon]_&_[data-sidebar=header-account]]:size-7 [[data-slot=sidebar][data-collapsible=icon]_&_[data-sidebar=header-account]]:justify-center [[data-slot=sidebar][data-collapsible=icon]_&_[data-sidebar=header-account]]:px-0 [[data-slot=sidebar][data-collapsible=icon]_&_[data-sidebar=header-account-label]]:hidden [[data-slot=sidebar][data-collapsible=icon]_&_[data-sidebar=header-account-chevron]]:hidden",
        className,
      )}
      {...props}
    />
  );
}

function SidebarFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn(
        "[display:flex] [flex-direction:column] [gap:0.5rem] [padding:0.5rem]",
        className,
      )}
      {...props}
    />
  );
}

function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn(
        "[margin-inline:0.5rem] [width:auto] [background-color:var(--sidebar-border)]",
        className,
      )}
      {...props}
    />
  );
}

function SidebarContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        "[display:flex] [min-height:0rem] [flex:1_1_0%] [flex-direction:column] [gap:0rem] [overflow:auto] [[data-slot=sidebar][data-collapsible=icon]_&]:[overflow:hidden]",
        className,
      )}
      {...props}
    />
  );
}

function SidebarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn(
        "[position:relative] [display:flex] [width:100%] [min-width:0rem] [flex-direction:column] [padding:0.5rem]",
        className,
      )}
      {...props}
    />
  );
}

function SidebarGroupLabel({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div"> & React.ComponentProps<"div">) {
  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(
      {
        className: cn(
          "[display:flex] [height:2rem] [flex-shrink:0] [align-items:center] [border-radius:0.5rem] [padding-inline:0.625rem] [font-size:0.75rem] [line-height:1rem] [font-weight:500] [color:var(--sidebar-foreground)] [[data-slot=sidebar][data-collapsible=icon]_&]:[opacity:0] outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:shadow-none [&>svg]:[flex-shrink:0]",
          className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "sidebar-group-label",
      sidebar: "group-label",
    },
  });
}

function SidebarGroupAction({
  className,
  render,
  ...props
}: useRender.ComponentProps<"button"> & React.ComponentProps<"button">) {
  return useRender({
    defaultTagName: "button",
    props: mergeProps<"button">(
      {
        className: cn(
          "[position:absolute] [top:0.875rem] [right:0.75rem] [display:flex] [width:1.25rem] [align-items:center] [justify-content:center] [border-radius:0.5rem] [padding:0rem] [color:var(--sidebar-foreground)] [[data-slot=sidebar][data-collapsible=icon]_&]:[display:none] outline-none hover:[color:var(--sidebar-accent-foreground)] focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:shadow-none [&>svg]:[flex-shrink:0]",
          className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "sidebar-group-action",
      sidebar: "group-action",
    },
  });
}

function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn(
        "[width:100%] [font-size:0.875rem] [line-height:1.25rem]",
        className,
      )}
      {...props}
    />
  );
}

function SidebarMenu({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn(
        "[display:flex] [width:100%] [min-width:0rem] [flex-direction:column] [gap:0rem]",
        className,
      )}
      {...props}
    />
  );
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn(
        "[isolation:isolate] [position:relative] [margin-block:var(--sidebar-menu-item-gap)]",
        className,
      )}
      {...props}
    />
  );
}

type SidebarMenuButtonVariantProps = {
  variant?: "default" | "outline";
  size?: "default" | "sm" | "lg";
};

const sidebarMenuButtonVariantClasses: Record<
  NonNullable<SidebarMenuButtonVariantProps["variant"]>,
  string
> = {
  default: "",
  outline: "border border-border-primary bg-bg-elevated",
};

const sidebarMenuButtonSizeClasses: Record<
  NonNullable<SidebarMenuButtonVariantProps["size"]>,
  string
> = {
  default: buttonVariants({
    variant: "inline",
    size: "provider",
    layout: "full",
  }),
  sm: buttonVariants({ variant: "inline", size: "sm", layout: "full" }),
  lg: buttonVariants({ variant: "inline", size: "lg", layout: "full" }),
};

function sidebarMenuButtonVariants({
  variant = "default",
  size = "default",
}: SidebarMenuButtonVariantProps = {}) {
  return cn(
    sidebarMenuButtonSizeClasses[size],
    sidebarMenuButtonVariantClasses[variant],
    "justify-start text-left text-text-primary data-[active]:bg-tint-secondary [&_svg]:text-text-secondary hover:[&_svg]:text-text-primary data-[active]:[&_svg]:text-text-primary [[data-slot=sidebar][data-collapsible=icon]_&]:justify-center [[data-slot=sidebar][data-collapsible=icon]_&]:px-0 [[data-slot=sidebar][data-collapsible=icon]_&>span]:hidden [[data-slot=sidebar][data-collapsible=icon]_&_svg:first-child]:mr-0",
  );
}

function SidebarMenuButton({
  render,
  isActive = false,
  variant = "default",
  size = "default",
  tooltip,
  className,
  children,
  ...props
}: useRender.ComponentProps<"button"> &
  React.ComponentProps<"button"> & {
    isActive?: boolean;
    tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  } & SidebarMenuButtonVariantProps) {
  const { isMobile, state } = useSidebar();
  const comp = useRender({
    defaultTagName: "button",
    props: mergeProps<"button">(
      {
        className: cn(sidebarMenuButtonVariants({ variant, size }), className),
        children,
      },
      props,
    ),
    render: !tooltip ? render : <TooltipTrigger render={render} />,
    state: {
      slot: "sidebar-menu-button",
      sidebar: "menu-button",
      size,
      active: isActive,
    },
  });

  if (!tooltip) {
    return comp;
  }

  if (typeof tooltip === "string") {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      {comp}
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== "collapsed" || isMobile}
        {...tooltip}
      />
    </Tooltip>
  );
}

function SidebarMenuAction({
  className,
  render,
  showOnHover = false,
  ...props
}: useRender.ComponentProps<"button"> &
  React.ComponentProps<"button"> & {
    showOnHover?: boolean;
  }) {
  return useRender({
    defaultTagName: "button",
    props: mergeProps<"button">(
      {
        className: cn(
          "[position:absolute] [top:0.375rem] [right:0.25rem] [display:flex] [width:1.25rem] [align-items:center] [justify-content:center] [border-radius:0.5rem] [padding:0rem] [color:var(--sidebar-foreground)] [[data-slot=sidebar][data-collapsible=icon]_&]:[display:none] outline-none hover:[color:var(--sidebar-accent-foreground)] focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:shadow-none [&>svg]:[flex-shrink:0]",
          showOnHover && "md:[opacity:0]",
          className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "sidebar-menu-action",
      sidebar: "menu-action",
    },
  });
}

function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        "[pointer-events:none] [position:absolute] [right:0.25rem] [display:flex] [height:1.25rem] [min-width:1.25rem] [align-items:center] [justify-content:center] [border-radius:0.5rem] [padding-inline:0.25rem] [font-size:0.75rem] [line-height:1rem] [font-weight:500] [color:var(--sidebar-foreground)] [font-variant-numeric:tabular-nums] [user-select:none] [[data-slot=sidebar][data-collapsible=icon]_&]:[display:none]",
        className,
      )}
      {...props}
    />
  );
}

function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<"div"> & {
  showIcon?: boolean;
}) {
  // Random width between 50 to 90%.
  const [width] = React.useState(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  });

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn(
        "[display:flex] [height:2rem] [align-items:center] [gap:0.5rem] [border-radius:0.5rem] [padding-inline:0.5rem]",
        className,
      )}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className={"[width:1rem] [height:1rem] [border-radius:0.5rem]"}
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className={
          "[height:1rem] [max-width:var(--skeleton-width)] [flex:1_1_0%]"
        }
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

function SidebarMenuSub({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        "[margin-inline:0.875rem] [display:flex] [min-width:0rem] [translate-x:1px] [flex-direction:column] [gap:0.25rem] [border-left-width:1px] [border-left-style:solid] [border-color:var(--sidebar-border)] [padding-inline:0.625rem] [padding-block:0.125rem] [[data-slot=sidebar][data-collapsible=icon]_&]:[display:none]",
        className,
      )}
      {...props}
    />
  );
}

function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn("[position:relative]", className)}
      {...props}
    />
  );
}

function SidebarMenuSubButton({
  render,
  size = "md",
  isActive = false,
  className,
  ...props
}: useRender.ComponentProps<"a"> &
  React.ComponentProps<"a"> & {
    size?: "sm" | "md";
    isActive?: boolean;
  }) {
  return useRender({
    defaultTagName: "a",
    props: mergeProps<"a">(
      {
        className: cn(
          "[display:flex] [height:1.75rem] [min-width:0rem] [translate-x:calc(1px_*_-1)] [align-items:center] [gap:0.5rem] [overflow:hidden] [border-radius:0.5rem] [padding-inline:0.5rem] [color:var(--sidebar-foreground)] [[data-slot=sidebar][data-collapsible=icon]_&]:[display:none] outline-none hover:[color:var(--sidebar-accent-foreground)] focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:shadow-none active:[color:var(--sidebar-accent-foreground)] disabled:[opacity:0.5] [&[data-size=md]]:[font-size:0.875rem] [&[data-size=md]]:[line-height:1.25rem] [&[data-size=sm]]:[font-size:0.75rem] [&[data-size=sm]]:[line-height:1rem] [&>span:last-child]:[overflow:hidden] [&>span:last-child]:[text-overflow:ellipsis] [&>span:last-child]:[white-space:nowrap] [&>svg]:[color:var(--sidebar-accent-foreground)]",
          className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "sidebar-menu-sub-button",
      sidebar: "menu-sub-button",
      size,
      active: isActive,
    },
  });
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
